<script lang="ts">
import type { Component } from 'vue';

export default {
  name: 'MenuDropdown',
};

export interface MenuItem {
  label: string;
  handler: (...params: any[]) => void;
  icon?: Component;
  /** Tailwind text color class for the label (e.g. tw:text-black, tw:text-error). Defaults to tw:text-black. */
  textColorClass?: string;
  /** Tailwind text color class for the icon (e.g. tw:text-black, tw:text-error). Defaults to tw:text-secondary. */
  iconColorClass?: string;
  /** When true, item is visually disabled and handler is not invoked. */
  disabled?: boolean;
  /** Optional data-testid for the list item anchor. */
  testId?: string;
}
</script>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { Menu } from 'lucide-vue-next';

const props = withDefaults(
  defineProps<{
    items: MenuItem[];
    /** Items rendered at the top of the menu with the same style as items; a separator is shown below when present. */
    prependItems?: MenuItem[];
    end?: boolean;
    top?: boolean;
    bottom?: boolean;
    left?: boolean;
    right?: boolean;
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  }>(),
  { size: 'md' },
);

const list = ref<HTMLUListElement | null>(null);

const defaultDropdownPosition = 'tw:dropdown-bottom tw:dropdown-end';

const menuSizeClass = computed(() => {
  const map = { xs: 'tw:menu-xs', sm: 'tw:menu-sm', md: 'tw:menu-md', lg: 'tw:menu-lg', xl: 'tw:menu-xl' } as const;
  return map[props.size];
});

const dropdownPosition = computed(() => {
  const positions: string[] = [];

  if (props.end) {
    positions.push('tw:dropdown-end');
  }
  if (props.top) {
    positions.push('tw:dropdown-top');
  }
  if (props.bottom) {
    positions.push('tw:dropdown-bottom');
  }
  if (props.left) {
    positions.push('tw:dropdown-left');
  }
  if (props.right) {
    positions.push('tw:dropdown-right');
  }

  return positions.length > 0 ? positions.join(' ') : defaultDropdownPosition;
});

function handleClick(item?: MenuItem) {
  list.value?.blur();
  item?.handler(item.label);
}
</script>

<template>
  <div
    class="tw:dropdown"
    :class="dropdownPosition">
    <slot name="action">
      <label
        tabindex="0"
        class="tw:btn tw:btn-xs">
        <Menu class="tw:h-4 tw:w-4" />
      </label>
    </slot>

    <ul
      tabindex="0"
      class="tw:dropdown-content tw:menu tw:z-99 tw:w-max tw:flex-nowrap tw:rounded-md tw:border tw:border-solid tw:border-gray-200 tw:bg-base-100 tw:p-2 tw:shadow-sm"
      :class="menuSizeClass"
      ref="list">
      <template v-if="prependItems?.length">
        <li
          v-for="item of prependItems"
          :key="item.label">
          <a
            class="tw:flex tw:items-center tw:gap-2"
            :class="[item.textColorClass ?? 'tw:text-black', item.disabled ? 'tw:pointer-events-none tw:opacity-50' : '']"
            :data-testid="item.testId"
            @click="
              () => {
                if (!item.disabled) handleClick(item);
              }
            ">
            <component
              v-if="item.icon"
              :is="item.icon"
              :class="['tw:h-4 tw:w-4 tw:shrink-0', item.iconColorClass ?? 'tw:text-secondary']" />
            {{ item.label }}
          </a>
        </li>
      </template>
      <slot name="prepend" />
      <slot
        name="items"
        :items="items"
        :handleClick="handleClick">
        <li
          v-for="item of items"
          :key="item.label">
          <a
            class="tw:flex tw:items-center tw:gap-2"
            :class="[item.textColorClass ?? 'tw:text-black', item.disabled ? 'tw:pointer-events-none tw:opacity-50' : '']"
            :data-testid="item.testId"
            @click="
              () => {
                if (!item.disabled) handleClick(item);
              }
            ">
            <component
              v-if="item.icon"
              :is="item.icon"
              :class="['tw:h-4 tw:w-4 tw:shrink-0', item.iconColorClass ?? 'tw:text-secondary']" />
            {{ item.label }}
          </a>
        </li>
      </slot>
    </ul>
  </div>
</template>
